import { useTranslation } from 'react-i18next'
import type { TicketCounts } from '@/api/ticketsApi'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface Kpi {
  label: string
  value: number | undefined
  /** No analytics endpoint in the MVP: shown as "—" with an explanation, never a made-up number. */
  analyticsOnly?: boolean
  danger?: boolean
}

/** Artboard 01 KPI strip: five equal cells divided by hairlines. */
export function KpiStrip({ counts }: { counts: TicketCounts | null | undefined }) {
  const { t } = useTranslation()
  const kpis: Kpi[] = [
    { label: t('queue.kpi.open'), value: counts?.allOpen },
    { label: t('queue.kpi.critical'), value: counts?.critical, danger: true },
    { label: t('queue.kpi.slaMet'), value: undefined, analyticsOnly: true },
    { label: t('queue.kpi.medianFirstResponse'), value: undefined, analyticsOnly: true },
    { label: t('queue.kpi.escalated'), value: counts?.escalated },
  ]
  return (
    <TooltipProvider delayDuration={150}>
      <section className="flex border-y border-line" aria-label={t('queue.kpi.open')}>
        {kpis.map((kpi, i) => {
          const value = (
            <span
              className={cn(
                'text-kpi font-semibold',
                kpi.analyticsOnly || kpi.value === undefined ? 'text-ink-muted' : kpi.danger ? 'text-priority-p1' : 'text-ink-strong',
              )}
            >
              {kpi.value ?? '—'}
            </span>
          )
          return (
            <div key={kpi.label} className={cn('flex grow basis-0 flex-col gap-1 py-4', i > 0 && 'border-l border-line pl-6')}>
              <span className="text-label text-ink-muted">{kpi.label}</span>
              {kpi.analyticsOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="w-fit cursor-help" aria-label={`${kpi.label}: ${t('queue.kpi.analyticsOnly')}`}>
                      {value}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t('queue.kpi.analyticsOnly')}</TooltipContent>
                </Tooltip>
              ) : (
                value
              )}
            </div>
          )
        })}
      </section>
    </TooltipProvider>
  )
}
